import React, { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, GitBranch, Wand2, Sparkles } from "lucide-react";

const DEPT_TYPES = [
  { value: "noc", label: "NOC / Technical" },
  { value: "billing", label: "Billing" },
  { value: "fraud", label: "Fraud Management" },
  { value: "support", label: "Customer Support" },
  { value: "sales", label: "Sales" },
  { value: "compliance", label: "Compliance" },
  { value: "admin", label: "Admin" },
  { value: "custom", label: "Custom" },
];

const AI_MODES = [
  { value: "draft", label: "Draft only" },
  { value: "approval", label: "Approval required" },
  { value: "hybrid", label: "Hybrid (auto for routine)" },
  { value: "automatic", label: "Fully automatic" },
  { value: "disabled", label: "Disabled" },
];

export default function Departments() {
  const { user, activeCompanyId } = useAuth();
  const [depts, setDepts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [presets, setPresets] = useState([]);
  const [open, setOpen] = useState(false);
  const [presetOpen, setPresetOpen] = useState(false);
  const [form, setForm] = useState({ company_id: "", name: "", code: "", dept_type: "support", description: "", ai_mode: "draft" });
  const [busyPreset, setBusyPreset] = useState("");

  const load = async () => {
    const params = activeCompanyId ? { company_id: activeCompanyId } : {};
    const [d, c, p] = await Promise.all([
      api.get("/departments", { params }),
      api.get("/companies"),
      api.get("/departments/presets"),
    ]);
    setDepts(d.data);
    setCompanies(c.data);
    setPresets(p.data);
    if (!form.company_id && (activeCompanyId || c.data[0])) {
      setForm((f) => ({ ...f, company_id: activeCompanyId || c.data[0]?.id }));
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeCompanyId]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/departments", form);
      toast.success("Department created");
      setOpen(false);
      setForm({ ...form, name: "", code: "", description: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const updateMode = async (id, ai_mode) => {
    try {
      await api.patch(`/departments/${id}`, { ai_mode });
      toast.success("Mode updated");
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const installPreset = async (preset) => {
    if (!form.company_id) { toast.error("Select a company first (top bar)"); return; }
    setBusyPreset(preset.key);
    try {
      await api.post("/departments/from-preset", { company_id: form.company_id, preset_key: preset.key });
      toast.success(`Department "${preset.name}" added`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
    setBusyPreset("");
  };

  return (
    <div className="flex flex-col h-full overflow-y-auto" data-testid="departments-page">
      <PageHeader
        title="Departments"
        subtitle="Group inboxes, teams, and AI agents by function. Configure escalation and response targets."
        actions={
          <div className="flex items-center gap-2">
            <Dialog open={presetOpen} onOpenChange={setPresetOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-sm" data-testid="preset-dept-button">
                  <Wand2 className="h-4 w-4 mr-1.5" /> Add from preset
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-sm max-w-2xl" data-testid="preset-dept-modal">
                <DialogHeader>
                  <DialogTitle className="font-display text-xl tracking-tight flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-terracotta" /> Add a curated department
                  </DialogTitle>
                </DialogHeader>
                <p className="text-xs text-muted-foreground">
                  Each preset spins up a department with a ready-to-use AI persona and auto-reply message.
                  You can edit everything after creation.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
                  {presets.map((p) => {
                    const exists = depts.some((d) => (d.code || "").toUpperCase() === (p.code || "").toUpperCase() && d.company_id === form.company_id);
                    return (
                      <button
                        key={p.key}
                        type="button"
                        disabled={exists || busyPreset === p.key}
                        onClick={() => installPreset(p)}
                        className={`text-left border rounded-sm p-3 transition-all ${exists ? "opacity-50 cursor-not-allowed" : "hover:shadow-md hover:-translate-y-0.5 hover:border-emerald-brand/40"}`}
                        data-testid={`preset-item-${p.key}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="font-medium text-sm">{p.name}</div>
                          <span className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">{p.dept_type}</span>
                        </div>
                        <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{p.description}</div>
                        <div className="text-[10px] font-mono text-muted-foreground mt-1.5">
                          {exists ? "Already added" : busyPreset === p.key ? "Adding…" : "Click to add"}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </DialogContent>
            </Dialog>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-sm" data-testid="new-department-button">
                  <Plus className="h-4 w-4 mr-1.5" /> Custom department
                </Button>
              </DialogTrigger>
            <DialogContent className="rounded-sm">
              <DialogHeader><DialogTitle className="font-display text-xl tracking-tight">Create department</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-3">
                <Field label="Company">
                  <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="dept-company-select"><SelectValue placeholder="Select company" /></SelectTrigger>
                    <SelectContent>
                      {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.trading_name || c.legal_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Name">
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="dept-name-input" />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Code">
                    <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="dept-code-input" />
                  </Field>
                  <Field label="Type">
                    <Select value={form.dept_type} onValueChange={(v) => setForm({ ...form, dept_type: v })}>
                      <SelectTrigger className="rounded-sm" data-testid="dept-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{DEPT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                </div>
                <Field label="AI mode">
                  <Select value={form.ai_mode} onValueChange={(v) => setForm({ ...form, ai_mode: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="dept-aimode-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{AI_MODES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Description">
                  <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="dept-description-input" />
                </Field>
                <DialogFooter><Button type="submit" className="rounded-sm" data-testid="dept-create-submit">Create</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        }
      />
      <div className="p-6 lg:p-8">
        <div className="border border-border rounded-sm overflow-hidden">
          <table className="tbl w-full" data-testid="departments-table">
            <thead>
              <tr><th>Department</th><th>Type</th><th>Company</th><th>AI mode</th><th>Response target</th><th>Confidence</th></tr>
            </thead>
            <tbody>
              {depts.map((d) => {
                const c = companies.find((x) => x.id === d.company_id);
                return (
                  <tr key={d.id} data-testid={`dept-row-${d.id}`}>
                    <td>
                      <div className="flex items-center gap-2">
                        <div className="h-7 w-7 rounded-sm bg-primary/10 text-primary grid place-items-center">
                          <GitBranch className="h-3.5 w-3.5" />
                        </div>
                        <div>
                          <div className="font-medium">{d.name}</div>
                          <div className="text-[11px] font-mono text-muted-foreground">{d.code || d.dept_type}</div>
                        </div>
                      </div>
                    </td>
                    <td className="text-muted-foreground uppercase text-xs font-mono">{d.dept_type}</td>
                    <td className="text-muted-foreground">{c?.trading_name || c?.legal_name || "—"}</td>
                    <td className="w-56">
                      <Select value={d.ai_mode} onValueChange={(v) => updateMode(d.id, v)}>
                        <SelectTrigger className="rounded-sm h-8 text-xs" data-testid={`dept-mode-${d.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{AI_MODES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="font-mono text-xs">{d.response_time_target_minutes}m</td>
                    <td className="font-mono text-xs">{Math.round(d.ai_confidence_threshold * 100)}%</td>
                  </tr>
                );
              })}
              {depts.length === 0 && <tr><td colSpan="6" className="text-center text-muted-foreground py-8">No departments yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
